import React, { useState } from 'react';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Image from '~components/atoms/Image';
import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';
import Text from '~components/atoms/Text';

import { TUpdateInventoryTransactionStatusReduxParams } from '~types/feat/approval';
import { FundTypeEnum, GoldTypeEnum, InventoryActionEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum, SilverTypeEnum, TransactionStatusEnum } from '~utils/enums';

import UtilFunctions from '~utils/functions';
import * as Styles from './_inventoryTransactionTable.module.scss';

const InventoryTransactionTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc: {
            renderTransactionStatusChip,
        },
    } = UtilFunctions;

    const inventoryTransactionListing = useAppSelector(state => state.gold.goldInventory.inventoryTransactionListing.data);
    const isLoading = useAppSelector(state => state.gold.loadings.getInventoryTransactionListing);
    const editAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.Edit)?.modules || [];

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.goldSetConfirmUpdateInventoryTransactionStatusModalIsOpen(open));
    const setTempUpdateInventoryTransactionState = (data: TUpdateInventoryTransactionStatusReduxParams) => dispatch(combinedActions.goldSetTempUpdateInventoryTransactionState(data));

    const handleCancelTransactionClick = (data: TUpdateInventoryTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateInventoryTransactionState(data);
    };

    const tableHeader = [
        'PRODUCT NAME',
        'TRANSACTION TYPE',
        'STATUS',
        'IMAGE',
        'AMOUNT',
        'PHYSICAL WEIGHT',
        'PAPER WEIGHT (G)',
        'SERIAL NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        // 'TOTAL COST',
        'INITIAL TOTAL PRICE',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'TRANSACTION DATE',
        editAccessModules.find(module => module === ModuleEnum.InventoryTransaction) ? 'ACTION' : null,
    ];

    const tableBody = inventoryTransactionListing.map(transaction => {
        const {
            productName,
            image,
            code,
            branchCode,
            branchName,
            fundType,
            action,
            // totalPrice,
            totalNetValue,
            createdAt,
            inventoryLogId,
            amount,
            status,
            metalType,
        } = transaction;

        const renderAction = () => {
            if (!action) return '-';

            if (action == InventoryActionEnum.TransferTo) return 'Transfer To';
            if (action == InventoryActionEnum.TransferFrom) return 'Transfer From';
            if (action == InventoryActionEnum.Restock) return 'Restock';
            if (action == InventoryActionEnum.NewProduct) return 'New Product';
            if (action == InventoryActionEnum.BankSell) return 'Bank Sell';
            if (action == InventoryActionEnum.BankBuy) return 'Bank Buy';
            if (action == InventoryActionEnum.RejectTransaction) return 'Reject Transaction';
            if (action == InventoryActionEnum.CancelTransaction) return 'Cancel Transaction';
            if (action == InventoryActionEnum.GoldConversion) return 'Gold Conversion';

            return InventoryActionEnum[action];
        };

        const massagedCode = code.map(serial => serial.serialNo).filter(item => item);

        const handleViewSerialNo = () => {
            setIsSerialNoModalOpen(true);
            setSerialNoList(massagedCode);
        };

        const renderSerialNo = () => {
            return (
                <ActionButton
                    testId='inventory-transactions-table-view-serialNo-modal'
                    key={inventoryLogId}
                    label='View'
                    onClick={() => handleViewSerialNo()}
                />
            );
        };

        return {
            cells: [
                productName,

                <Text
                    key={inventoryLogId}
                    label={renderAction() || ''}
                    className={Styles['transaction-action-label']}
                    isBold
                />,

                renderTransactionStatusChip(status, TransactionStatusEnum[status]),

                <Image
                    key={inventoryLogId}
                    src={image}
                    width={40}
                    height={40}
                />,

                (() => {
                    if (metalType === MetalTypeEnum.Gold) {
                        return transaction.goldType === GoldTypeEnum.PhysicalGold ? amount ?? 0 : 'N/A';
                    }
                    return transaction.silverType === SilverTypeEnum.PhysicalSilver ? amount ?? 0 : 'N/A';
                })(),

                `${metalType === MetalTypeEnum.Gold
                    ? (transaction.physicalGoldWeight ?? 0)
                    : (transaction.physicalSilverWeight ?? 0)
                } g`,

                `${metalType === MetalTypeEnum.Gold
                    ? (transaction.paperGoldWeight ?? 0)
                    : (transaction.paperSilverWeight ?? 0)
                } g`,

                renderSerialNo(),

                branchCode,
                branchName,

                // typeof totalPrice === 'number'
                //     ? `RM${(Math.round(totalPrice * 100) / 100).toFixed(2)}`
                //     : '-',

                typeof totalNetValue === 'number'
                    ? `RM${(Math.round(totalNetValue * 100) / 100).toFixed(2)}`
                    : '-',

                FundTypeEnum[fundType] || '-',

                metalType === MetalTypeEnum.Gold
                    ? (transaction.goldType
                        ? GoldTypeEnum[transaction.goldType].split(/(?=[A-Z])/).join(' ')
                        : '')
                    : '-',

                metalType === MetalTypeEnum.Silver
                    ? (transaction.silverType
                        ? SilverTypeEnum[transaction.silverType].split(/(?=[A-Z])/).join(' ')
                        : '')
                    : '-',

                dayjs(createdAt).format('DD/MM/YYYY hh:mm A'),

                editAccessModules.includes(ModuleEnum.InventoryTransaction) &&
                status === TransactionStatusEnum.Approved
                    ? (
                        <ActionButton
                            key={inventoryLogId}
                            className={Styles['cancel-transaction-button']}
                            label='Cancel Transaction'
                            onClick={() =>
                                handleCancelTransactionClick({
                                    inventoryLogId,
                                    status: TransactionStatusEnum.Cancelled,
                                })
                            }
                        />
                    )
                    : '-',
            ],

            className: metalType === MetalTypeEnum.Gold
                ? Styles['gold-row']
                : '',
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={isLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default InventoryTransactionTable;
